import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSkillsThread, useSendSkillsMessage, useApplySkillEdit } from "../hooks/useSkills";
import { ChatThread } from "../components/chat/ChatThread";
import { ChatInput } from "../components/chat/ChatInput";
import { SkillEditBanner } from "../components/skills/SkillEditBanner";
import { Button } from "../components/ui/Button";

export function Skills() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [appliedEdits, setAppliedEdits] = useState<Set<string>>(new Set());
  const [pendingEdit, setPendingEdit] = useState<{
    slug: string;
    name: string;
    content: string;
  } | null>(null);

  const { data: threadData, isLoading, error } = useSkillsThread(threadId);
  const sendMessage = useSendSkillsMessage(threadId || "");
  const applyEdit = useApplySkillEdit(threadId || "");

  const thread = threadData?.data;
  const messages = thread?.messages ?? [];

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isWaitingForAI = lastMessage?.role === "user" || sendMessage.isPending;

  const handleSend = async (content: string) => {
    if (!threadId) return;
    const result = await sendMessage.mutateAsync({ content });
    // Check if the response contains a skill edit suggestion
    if (result.data.skill_edit) {
      setPendingEdit(result.data.skill_edit);
    }
  };

  const handleApplyEdit = async () => {
    if (!pendingEdit || !threadId) return;
    try {
      await applyEdit.mutateAsync({
        skill_slug: pendingEdit.slug,
        skill_name: pendingEdit.name,
        new_content: pendingEdit.content,
      });
      setAppliedEdits((prev) => new Set(prev).add(pendingEdit.slug));
      setPendingEdit(null);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <p className="mb-2 text-sm text-red-600">
          {error ? "Failed to load skills thread." : "Thread not found."}
        </p>
        <Button variant="secondary" size="sm" onClick={() => navigate("/brand")}>
          Back to brand
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-slate-900">
              Skills Coach
            </h1>
            <p className="text-xs text-slate-500">
              Chat to view, edit, or create marketing skills for your brand
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => navigate("/brand")}>
            Done
          </Button>
        </div>

        {/* Pending skill edit banner */}
        {pendingEdit && (
          <SkillEditBanner
            slug={pendingEdit.slug}
            name={pendingEdit.name}
            onApply={handleApplyEdit}
            applying={applyEdit.isPending}
            applied={appliedEdits.has(pendingEdit.slug)}
          />
        )}
      </div>

      {/* Messages */}
      <ChatThread messages={messages} isWaiting={isWaitingForAI} />

      {/* Input */}
      <ChatInput onSend={handleSend} disabled={sendMessage.isPending} />
    </div>
  );
}
